import React, { useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { unsubscribe } from '../api';
import { showToast } from '../toaster';

const reasons = [
  'I do not understand the amount on my bank statement',
  'I wish to receive the invoice of my order',
  'I wish to cancel',
  'I want to unsubscribe',
  'Other',
];

export default function ContactForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [iban, setIban] = useState('');
  const [reasonIdx, setReasonIdx] = useState(0);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setIban('');
    setReasonIdx(0);
    setMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const isUnsub = reasonIdx === 2 || reasonIdx === 3;
    try {
      const result = await unsubscribe({
        first_name: firstName,
        last_name: lastName,
        email,
        iban_last_5: iban,
        is_unsubscribe: isUnsub,
        message,
        options: { reason: reasons[reasonIdx] },
      });
      showToast(
        isUnsub ? 'Unsubscribe request sent successfully.' : 'Message sent successfully.',
        'success'
      );
      reset();
    } catch (err: any) {
      let msg = err?.message || 'Something went wrong.';
      // If error is JSON, try to extract message
      try {
        const parsed = JSON.parse(msg);
        if (parsed && typeof parsed.message === 'string') msg = parsed.message;
      } catch {}
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_70px_-35px_rgba(15,23,42,0.38)] overflow-hidden">
      <div className="border-b border-slate-200 bg-slate-950 px-6 py-5 text-white">
        <h3 className="text-lg font-semibold tracking-tight">Contact us</h3>
        <p className="mt-1 text-sm text-slate-300">Our operators will answer your questions quickly and efficiently.</p>
      </div>
      <form className="space-y-4 p-6 text-sm" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <input
            aria-label="First name"
            placeholder="First name *"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 transition"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            required
          />
          <input
            aria-label="Last name"
            placeholder="Last name *"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 transition"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            required
          />
        </div>
        <input
          type="email"
          aria-label="Email"
          placeholder="Email *"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 transition"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />
        <input
          aria-label="IBAN"
          placeholder="Last 5 digits of IBAN *"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 transition"
          value={iban}
          onChange={e => setIban(e.target.value)}
          required
          maxLength={5}
        />
        <select
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-slate-700 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 transition"
          value={reasonIdx}
          onChange={e => setReasonIdx(Number(e.target.value))}
        >
          {reasons.map((r, i) => (
            <option value={i} key={r}>{`${i + 1} - ${r}`}</option>
          ))}
        </select>
        <textarea
          aria-label="Message"
          placeholder="Your message *"
          className="h-24 w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 transition"
          value={message}
          onChange={e => setMessage(e.target.value)}
          required
        />
        <button
          type="submit"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
          disabled={loading}
        >
          <FiSend className="w-4 h-4" /> {loading ? 'SENDING...' : 'SEND MESSAGE'}
        </button>
      </form>
    </div>
  );
}